/**
 * Guide note for the admin panel: lists CMS embed codes ({{phone-number}}, etc.)
 * that are replaced with values from Settings & Branding. Use in CMS page body
 * and custom header/footer HTML.
 */

import { Info } from 'lucide-react';
import { CMS_PLACEHOLDER_KEYS } from '@/lib/cms-placeholders';

interface AdminCmsPlaceholderGuideProps {
  /** Optional title override. */
  title?: string;
  /** Optional short context (e.g. "Use in page body or custom header/footer HTML"). */
  context?: string;
  /** Compact layout (single line of codes). Default: false (block with codes on separate line). */
  compact?: boolean;
  className?: string;
}

const PLACEHOLDER_LABELS: Record<(typeof CMS_PLACEHOLDER_KEYS)[number], string> = {
  'phone-number': 'Site contact phone',
  'contact-email': 'Site contact email',
  'company-address': 'Company address',
  'brand-name': 'Brand name',
  'brand-tagline': 'Brand tagline',
};

/** Additional embed shortcodes (render as React components) */
const EMBED_SHORTCODES: { key: string; label: string }[] = [
  { key: 'blog-articles', label: 'Blog articles listing' },
  { key: 'blog-articles-featured', label: 'Featured blog articles listing' },
];

export function AdminCmsPlaceholderGuide({
  title = 'CMS embed codes',
  context = 'Use in CMS page body or custom header/footer HTML; replaced with values from Settings & Branding.',
  compact = false,
  className = '',
}: AdminCmsPlaceholderGuideProps): JSX.Element {
  return (
    <div
      className={`rounded-lg border border-blue-200 bg-blue-50/60 p-3 ${className}`}
      role="note"
      aria-label="CMS embed codes guide"
    >
      <p className="text-xs font-medium text-blue-900 mb-1.5 flex items-center gap-1.5">
        <Info size={14} className="text-blue-600 shrink-0" />
        {title}
      </p>
      <p className="text-xs text-blue-800 mb-2">{context}</p>
      <div
        className={
          compact
            ? 'flex flex-wrap gap-x-2 gap-y-1 text-xs font-mono text-blue-900'
            : 'flex flex-wrap gap-x-3 gap-y-1.5 text-xs font-mono text-blue-900'
        }
      >
        {CMS_PLACEHOLDER_KEYS.map((key) => (
          <span key={key} className="flex items-baseline gap-1">
            <code className="bg-white/80 px-1.5 py-0.5 rounded border border-blue-100">
              {`{{${key}}}`}
            </code>
            {!compact && (
              <span className="text-blue-700 font-normal hidden sm:inline">
                → {PLACEHOLDER_LABELS[key]}
              </span>
            )}
          </span>
        ))}
        {/* Component embed shortcodes */}
        {EMBED_SHORTCODES.map(({ key, label }) => (
          <span key={key} className="flex items-baseline gap-1">
            <code className="bg-white/80 px-1.5 py-0.5 rounded border border-blue-100">
              {`{{${key}}}`}
            </code>
            {!compact && (
              <span className="text-blue-700 font-normal hidden sm:inline">
                → {label}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}
